import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { ticketsAPI, conversationsAPI, masterAPI, faqAPI } from '../../services/api';
import StatusBadge from '../../ui/StatusBadge';
import PriorityBadge from '../../ui/PriorityBadge';
import Icon from '../../ui/Icon';
import Avatar from '../../ui/Avatar';
import ToastContainer from '../../ui/ToastContainer';
import FloatingAssistantsHub from '../chat/FloatingAssistantsHub';
import { REQUESTER_CHAT_CONFIG } from '../chat/AIChatbot';
import { REQUESTER_VOICE_CONFIG } from '../chat/VoiceCallWidget';
import { formatInTz } from '../../utils/timezone';

function toast(message, type = 'success') {
  window.dispatchEvent(new CustomEvent('app:toast', { detail: { id: Date.now() + Math.random(), message, type } }));
}

/** Safely render email HTML: strip script/style, keep layout */
function SafeHtml({ html }) {
  if (!html || !html.trim()) return null;
  if (!/<[a-z][\s\S]*>/i.test(html)) {
    return <span style={{ whiteSpace: 'pre-wrap' }}>{html}</span>;
  }
  const safe = html
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/on\w+="[^"]*"/gi, '')
    .replace(/on\w+='[^']*'/gi, '');
  return (
    <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.6 }}
      dangerouslySetInnerHTML={{ __html: safe }} />
  );
}

const PRIORITIES      = ['Low', 'Medium', 'High', 'Urgent'];
const TYPES           = ['Incident', 'Service Request', 'Change', 'Problem'];
const STATUS_FILTERS  = ['All', 'Open', 'Pending Action', 'Resolved', 'Closed', 'Completed'];
const CLOSED_STATUSES = ['Resolved', 'Closed', 'Completed'];
const DATE_PRESETS    = ['Today', 'This Week', 'This Month', 'This Year'];
const PRIORITY_OPTS   = ['All', 'Low', 'Medium', 'High', 'Urgent'];

/* ── User-facing FAQ Browser ─────────────────────────────────────────── */
function UserFAQBrowser() {
  const [cats, setCats] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searching, setSearching] = useState(false);
  const [activeCat, setActiveCat] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [feedbackGiven, setFeedbackGiven] = useState({});
  const searchTimer = useRef(null);

  const loadFaqs = useCallback(async (catId) => {
    setLoading(true);
    try {
      const res = await faqAPI.list({ category_id: catId || undefined, limit: 100 });
      setFaqs(res.faqs || []);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const c = await faqAPI.listCategories();
        setCats(c);
      } catch {}
      loadFaqs(null);
    })();
  }, [loadFaqs]);

  const handleSearch = (q) => {
    setSearch(q);
    clearTimeout(searchTimer.current);
    if (!q.trim()) { loadFaqs(activeCat); return; }
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await faqAPI.search(q, 10);
        setFaqs(Array.isArray(res) ? res : []);
      } catch {} finally { setSearching(false); }
    }, 400);
  };

  const handleCat = (id) => {
    setActiveCat(id);
    setSearch('');
    loadFaqs(id);
  };

  const handleFeedback = async (id, helpful) => {
    if (feedbackGiven[id] !== undefined) return;
    setFeedbackGiven(prev => ({ ...prev, [id]: helpful }));
    try { await faqAPI.feedback(id, helpful); } catch {}
  };

  const CAT_COLORS = ['#3B5BDB', '#059669', '#D97706', '#7C3AED', '#E53E3E', '#0EA5E9', '#F97316', '#10B981'];
  const getCatIdx = (id) => cats.findIndex(c => c.id === id);
  const catColor  = (i) => CAT_COLORS[(i < 0 ? 0 : i) % CAT_COLORS.length];

  return (
    <main style={{ maxWidth: 1040, margin: '0 auto', padding: '28px 20px' }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1A202C', margin: 0 }}>Knowledge Base</h1>
        <p style={{ fontSize: 13, color: '#6B7280', margin: '4px 0 0' }}>Browse answers to common IT questions and DLP policies</p>
      </div>

      {/* Search */}
      <div style={{ position: 'relative', marginBottom: 20 }}>
        <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
          <Icon name="search" size={16} color="#9CA3AF" />
        </span>
        <input
          value={search}
          onChange={e => handleSearch(e.target.value)}
          placeholder="Search the knowledge base... (e.g. 'DLP', 'password reset', 'VPN')"
          style={{ width: '100%', padding: '13px 14px 13px 44px', border: '1.5px solid #E2E8F0', borderRadius: 12, fontSize: 14, background: '#fff', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', boxShadow: '0 2px 8px rgba(59,91,219,0.06)', color: '#1A202C' }}
        />
        {searching && <span style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: '#9CA3AF' }}>Searching...</span>}
      </div>

      {/* Category pills */}
      {cats.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 24 }}>
          <button onClick={() => handleCat(null)}
            style={{ padding: '7px 16px', borderRadius: 20, fontSize: 12, fontWeight: 700, border: `1.5px solid ${activeCat === null ? 'var(--accent)' : '#E2E8F0'}`, background: activeCat === null ? 'var(--accent)' : '#F8FAFF', color: activeCat === null ? '#fff' : '#374151', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' }}>
            All
          </button>
          {cats.map((c, i) => (
            <button key={c.id} onClick={() => handleCat(c.id)}
              style={{ padding: '7px 16px', borderRadius: 20, fontSize: 12, fontWeight: 700, border: `1.5px solid ${activeCat === c.id ? catColor(i) : '#E2E8F0'}`, background: activeCat === c.id ? catColor(i) : '#F8FAFF', color: activeCat === c.id ? '#fff' : '#374151', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' }}>
              {c.name}{c.faq_count > 0 && <span style={{ marginLeft: 5, opacity: 0.75, fontSize: 11 }}>({c.faq_count})</span>}
            </button>
          ))}
        </div>
      )}

      {/* FAQ accordion */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '48px 0', color: '#9CA3AF', fontSize: 14 }}>Loading...</div>
      ) : faqs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#9CA3AF' }}>
          <div style={{ fontSize: 42, marginBottom: 12 }}>🔍</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#374151', marginBottom: 6 }}>No results found</div>
          <div style={{ fontSize: 13 }}>Try different keywords or browse by category</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {faqs.map(faq => {
            const ci = getCatIdx(faq.category_id);
            const cc = catColor(ci);
            const isOpen = expanded === faq.id;
            const fb = feedbackGiven[faq.id];
            return (
              <div key={faq.id} style={{ background: '#fff', borderRadius: 14, border: `1.5px solid ${isOpen ? 'var(--accent)' : '#E2E8F0'}`, overflow: 'hidden', boxShadow: isOpen ? '0 4px 24px rgba(59,91,219,0.10)' : '0 1px 4px rgba(0,0,0,0.04)', transition: 'all 0.18s' }}>
                <div onClick={() => setExpanded(isOpen ? null : faq.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 20px', cursor: 'pointer', userSelect: 'none' }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: `${cc}18`, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon name="bulb" size={20} color={cc} />
                  </div>
                  <div style={{ flex: 1 }}>
                    {faq.category_name && (
                      <div style={{ fontSize: 10, fontWeight: 700, color: cc, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 3 }}>{faq.category_name}</div>
                    )}
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#1A202C', lineHeight: 1.4 }}>{faq.question}</div>
                  </div>
                  <div style={{ color: '#9CA3AF', fontSize: 18, flexShrink: 0, transition: 'transform 0.18s', transform: isOpen ? 'rotate(180deg)' : 'none' }}>▾</div>
                </div>

                {isOpen && (
                  <div style={{ borderTop: '1px solid #F1F5F9', padding: '18px 20px', background: '#FAFCFF' }}>
                    <div style={{ fontSize: 14, color: '#374151', lineHeight: 1.75, whiteSpace: 'pre-wrap', marginBottom: faq.tags?.length > 0 ? 14 : 18 }}>{faq.answer}</div>
                    {faq.tags?.length > 0 && (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
                        {faq.tags.map((tag, i) => (
                          <span key={i} style={{ padding: '2px 10px', borderRadius: 10, background: '#EEF2FF', color: 'var(--accent)', fontSize: 11, fontWeight: 600 }}>{tag}</span>
                        ))}
                      </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 12, borderTop: '1px solid #F1F5F9' }}>
                      <span style={{ fontSize: 12, color: '#6B7280', fontWeight: 600 }}>Was this helpful?</span>
                      {fb === undefined ? (
                        <>
                          <button onClick={() => handleFeedback(faq.id, true)}
                            style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 13px', borderRadius: 8, border: '1px solid #D1FAE5', background: '#ECFDF5', color: '#059669', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                            👍 Yes
                          </button>
                          <button onClick={() => handleFeedback(faq.id, false)}
                            style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 13px', borderRadius: 8, border: '1px solid #FEE2E2', background: '#FEF2F2', color: '#EF4444', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                            👎 No
                          </button>
                        </>
                      ) : (
                        <span style={{ fontSize: 12, color: fb ? '#059669' : '#EF4444', fontWeight: 700 }}>
                          {fb ? "👍 Thanks for your feedback!" : "👎 Sorry this wasn't helpful."}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </main>
  );
}

export default function RequesterPortal({ currentUser, onLogout }) {
  const [tickets, setTickets]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [statusFilter, setStatus] = useState('All');
  const [search, setSearch]       = useState('');
  const [showForm, setShowForm]   = useState(false);
  const [selected, setSelected]   = useState(null);
  const [csatTicket, setCsatTicket] = useState(null);

  // ── Date & priority filters ──────────────────────────────────────────────
  const [datePreset, setDatePreset]   = useState(null); // 'Today'|'This Week'|'This Month'|'This Year'|'custom'
  const [dateFrom, setDateFrom]       = useState('');
  const [dateTo, setDateTo]           = useState('');
  const [priorityFilter, setPriority] = useState('All');
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [activeSection, setActiveSection] = useState('tickets'); // 'tickets' | 'faq'

  // create form
  const [form, setForm]     = useState({
    subject: '', description: '', priority: 'Medium', type: 'Incident',
    category: '', subCategory: '', additionalSubCategory: '',
    department: currentUser?.department || '',
    orgUnit: '', headOfDepartment: '',
  });
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [formErr, setFormErr] = useState({});
  const [masterData, setMasterData] = useState({});

  // Load master data for category dropdowns once
  useEffect(() => {
    masterAPI.all()
      .then(grouped => { setMasterData(grouped || {}); })
      .catch(() => {});
  }, []);

  // Sub-categories filtered by selected category
  const filteredSubCategories = useMemo(() => {
    const subs = masterData.sub_category || [];
    if (!form.category) return subs;
    return subs.filter(s => !s.parent_value || s.parent_value === form.category);
  }, [form.category, masterData.sub_category]);

  // Additional sub-categories filtered by selected sub-category
  const filteredAdditionalSubCategories = useMemo(() => {
    const adds = masterData.additional_sub_category || [];
    if (!form.subCategory) return adds;
    return adds.filter(s => !s.parent_value || s.parent_value === form.subCategory);
  }, [form.subCategory, masterData.additional_sub_category]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ticketsAPI.list({
        status:  statusFilter !== 'All' ? statusFilter : undefined,
        search:  search || undefined,
        limit:   200,
        sortKey: 'created_at',
        sortDir: 'desc',
      });
      setTickets(res.tickets || []);
    } catch {
      toast('Could not load tickets', 'error');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);

  useEffect(() => { load(); }, [load]);

  const handleSelectTicket = (t) => {
    setSelected(t);
    // Auto-prompt CSAT for closed tickets not yet rated
    if (CLOSED_STATUSES.includes(t.status) && !t.request_rating) {
      setCsatTicket(t);
    }
  };

  // ── Derived: apply date + priority on top of already-loaded tickets ────────
  const filteredTickets = useMemo(() => {
    let result = tickets;

    // Priority
    if (priorityFilter !== 'All') {
      result = result.filter(t => t.priority === priorityFilter);
    }

    // Date range
    const now = new Date();
    let fromDate = null, toDate = null;
    if (datePreset === 'Today') {
      fromDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
      toDate   = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    } else if (datePreset === 'This Week') {
      const d = now.getDay();
      fromDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - d, 0, 0, 0, 0);
      toDate   = new Date(now.getFullYear(), now.getMonth(), now.getDate() + (6 - d), 23, 59, 59, 999);
    } else if (datePreset === 'This Month') {
      fromDate = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
      toDate   = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
    } else if (datePreset === 'This Year') {
      fromDate = new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0);
      toDate   = new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999);
    } else if (datePreset === 'custom') {
      if (dateFrom) fromDate = new Date(dateFrom + 'T00:00:00');
      if (dateTo)   toDate   = new Date(dateTo   + 'T23:59:59');
    }

    if (fromDate || toDate) {
      result = result.filter(t => {
        const d = t._created_at ? new Date(t._created_at) : null;
        if (!d) return false;
        if (fromDate && d < fromDate) return false;
        if (toDate   && d > toDate)   return false;
        return true;
      });
    }

    return result;
  }, [tickets, datePreset, dateFrom, dateTo, priorityFilter]);

  const activeFilterCount = (priorityFilter !== 'All' ? 1 : 0) + (datePreset ? 1 : 0);

  function clearAllFilters() {
    setPriority('All');
    setDatePreset(null);
    setDateFrom('');
    setDateTo('');
    setShowCustomRange(false);
  }

  function handlePreset(label) {
    if (datePreset === label) {
      setDatePreset(null);
      setShowCustomRange(false);
    } else {
      setDatePreset(label);
      setShowCustomRange(false);
    }
  }

  function handleCustomToggle() {
    if (showCustomRange) {
      setShowCustomRange(false);
      if (datePreset === 'custom') setDatePreset(null);
    } else {
      setShowCustomRange(true);
      setDatePreset('custom');
      DATE_PRESETS.forEach(() => {}); // clear preset highlight
    }
  }

  const stats = {
    total:    filteredTickets.length,
    open:     filteredTickets.filter(t => t.status === 'Open').length,
    pending:  filteredTickets.filter(t => t.status?.startsWith('Pending')).length,
    resolved: filteredTickets.filter(t => CLOSED_STATUSES.includes(t.status)).length,
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.subject.trim()) errs.subject = true;
    if (!form.department.trim()) errs.department = true;
    if (!form.category.trim()) errs.category = true;
    if (Object.keys(errs).length) { setFormErr(errs); return; }
    setSaving(true);
    try {
      const ticket = await ticketsAPI.create({
        subject:                  form.subject.trim(),
        description:              form.description.trim() || undefined,
        priority:                 form.priority,
        type:                     form.type,
        source:                   'Portal',
        requester_name:           currentUser.name,
        requester_email:          currentUser.email,
        department:               form.department.trim(),
        org_unit:                 form.orgUnit.trim() || undefined,
        head_of_department:       form.headOfDepartment.trim() || undefined,
        category:                 form.category.trim(),
        sub_category:             form.subCategory || undefined,
        additional_sub_category:  form.additionalSubCategory || undefined,
      });

      // Upload attachments as an initial conversation entry if any files selected
      if (attachmentFiles.length > 0) {
        try {
          await conversationsAPI.create(
            ticket.id,
            { mode: 'requester_reply', body: form.description.trim() || '(See attachments)' },
            attachmentFiles
          );
        } catch {
          toast('Ticket created but attachments could not be uploaded', 'warning');
        }
      }

      toast('Ticket submitted successfully');
      setForm({
        subject: '', description: '', priority: 'Medium', type: 'Incident',
        category: '', subCategory: '', additionalSubCategory: '',
        department: currentUser?.department || '',
        orgUnit: '', headOfDepartment: '',
      });
      setAttachmentFiles([]);
      setFormErr({});
      setShowForm(false);
      load();
    } catch (err) {
      toast(err.message || 'Failed to submit ticket', 'error');
    } finally {
      setSaving(false);
    }
  };

  const fmt = (iso) => iso
    ? formatInTz(iso, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })
    : '"';

  return (
    <div style={{ minHeight: '100vh', background: '#F0F4FF', fontFamily: "'DM Sans', sans-serif" }}>
      <ToastContainer />

      {/* Top bar */}
      <header style={{ background: '#fff', borderBottom: '1px solid #E2E8F0', padding: '0 24px', height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 50, boxShadow: '0 1px 4px rgba(59,91,219,0.07)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: 9, background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon name="ticket" size={16} color="#fff" />
          </div>
          <span style={{ fontSize: 16, fontWeight: 700, color: '#1A202C', letterSpacing: '-0.3px' }}>Support Portal</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar name={currentUser?.name} size={30} />
          <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{currentUser?.name}</span>
          <button onClick={onLogout} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', border: '1px solid #E2E8F0', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 12, color: '#6B7280', fontFamily: 'inherit' }}>
            <Icon name="logout" size={13} color="#6B7280" /> Sign out
          </button>
        </div>
      </header>

      {/* ── Section Navigation ── */}
      <div style={{ background: '#fff', borderBottom: '1px solid #E2E8F0', padding: '0 24px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <div style={{ maxWidth: 1040, margin: '0 auto', display: 'flex' }}>
          {[{ id: 'tickets', label: 'My Tickets' }, { id: 'faq', label: 'Knowledge Base' }].map(sec => (
            <button key={sec.id} onClick={() => setActiveSection(sec.id)}
              style={{ padding: '13px 20px', background: 'none', border: 'none', borderBottom: `2.5px solid ${activeSection === sec.id ? 'var(--accent)' : 'transparent'}`, color: activeSection === sec.id ? 'var(--accent)' : '#6B7280', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s', whiteSpace: 'nowrap' }}>
              {sec.label}
            </button>
          ))}
        </div>
      </div>

      {activeSection === 'tickets' && (
      <main style={{ maxWidth: 1040, margin: '0 auto', padding: '28px 20px' }}>

        {/* Title + New Ticket */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1A202C', margin: 0 }}>My Tickets</h1>
            <p style={{ fontSize: 13, color: '#6B7280', margin: '4px 0 0' }}>Track and manage your support requests</p>
          </div>
          <button onClick={() => setShowForm(v => !v)} style={{ display: 'flex', alignItems: 'center', gap: 7, background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 2px 8px rgba(59,91,219,0.25)' }}>
            <Icon name="plus" size={14} color="#fff" /> New Ticket
          </button>
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 24 }}>
          {[
            { label: 'Total',    value: stats.total,    color: '#3B5BDB', bg: '#EEF2FF' },
            { label: 'Open',     value: stats.open,     color: '#059669', bg: '#ECFDF5' },
            { label: 'Pending',  value: stats.pending,  color: '#D97706', bg: '#FFFBEB' },
            { label: 'Resolved', value: stats.resolved, color: '#6B7280', bg: '#F9FAFB' },
          ].map(s => (
            <div key={s.label} style={{ background: s.bg, border: `1px solid ${s.color}22`, borderRadius: 12, padding: '16px 20px' }}>
              <div style={{ fontSize: 26, fontWeight: 800, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2, fontWeight: 600 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Create Form */}
        {showForm && (
          <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #E2E8F0', padding: '24px', marginBottom: 24, boxShadow: '0 4px 20px rgba(59,91,219,0.09)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
              <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#1A202C' }}>Submit a New Ticket</h2>
              <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><Icon name="close" size={16} color="#9CA3AF" /></button>
            </div>
            <form onSubmit={handleCreate}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 18px' }}>
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={lStyle}>Subject <span style={{ color: '#E53E3E' }}>*</span></label>
                  <input value={form.subject} onChange={e => { setForm(f => ({ ...f, subject: e.target.value })); setFormErr(fe => ({ ...fe, subject: false })); }} placeholder="Briefly describe the issue..." style={inputStyle(formErr.subject)} />
                  {formErr.subject && <div style={errStyle}>Subject is required</div>}
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={lStyle}>Description</label>
                  <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Provide as much detail as possible..." rows={4} style={{ ...inputStyle(), resize: 'vertical', minHeight: 90 }} />
                </div>
                <div>
                  <label style={lStyle}>Priority</label>
                  <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))} style={inputStyle()}>{PRIORITIES.map(p => <option key={p}>{p}</option>)}</select>
                </div>
                <div>
                  <label style={lStyle}>Type</label>
                  <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} style={inputStyle()}>{TYPES.map(t => <option key={t}>{t}</option>)}</select>
                </div>
                <div>
                  <label style={lStyle}>Department <span style={{ color: '#E53E3E' }}>*</span></label>
                  <select
                    value={form.department}
                    onChange={e => { setForm(f => ({ ...f, department: e.target.value })); setFormErr(fe => ({ ...fe, department: false })); }}
                    style={inputStyle(formErr.department)}
                  >
                    <option value="">— Select Department —</option>
                    {(masterData.department || []).map(d => <option key={d.id} value={d.value}>{d.value}</option>)}
                  </select>
                  {formErr.department && <div style={errStyle}>Department is required</div>}
                </div>
                <div>
                  <label style={lStyle}>Organization Unit</label>
                  <select value={form.orgUnit} onChange={e => setForm(f => ({ ...f, orgUnit: e.target.value }))} style={inputStyle()}>
                    <option value="">— Select Organization Unit —</option>
                    {(masterData.org_unit || []).map(o => <option key={o.id} value={o.value}>{o.value}</option>)}
                  </select>
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={lStyle}>Head of Department</label>
                  <select value={form.headOfDepartment} onChange={e => setForm(f => ({ ...f, headOfDepartment: e.target.value }))} style={inputStyle()}>
                    <option value="">— Select Head of Department —</option>
                    {(masterData.head_of_department || []).map(h => <option key={h.id} value={h.value}>{h.value}</option>)}
                  </select>
                </div>
                {/* Category */}
                <div>
                  <label style={lStyle}>Category <span style={{ color: '#E53E3E' }}>*</span></label>
                  <select
                    value={form.category}
                    onChange={e => { setForm(f => ({ ...f, category: e.target.value, subCategory: '', additionalSubCategory: '' })); setFormErr(fe => ({ ...fe, category: false })); }}
                    style={inputStyle(formErr.category)}
                  >
                    <option value="">— Select Category —</option>
                    {(masterData.category || []).map(c => <option key={c.id} value={c.value}>{c.value}</option>)}
                  </select>
                  {formErr.category && <div style={errStyle}>Category is required</div>}
                </div>
                {/* Sub-Category */}
                <div>
                  <label style={lStyle}>Sub-Category</label>
                  <select value={form.subCategory} onChange={e => setForm(f => ({ ...f, subCategory: e.target.value, additionalSubCategory: '' }))} style={inputStyle()} disabled={!form.category && filteredSubCategories.length === 0}>
                    <option value="">— Select Sub-Category —</option>
                    {filteredSubCategories.map(c => <option key={c.id} value={c.value}>{c.value}</option>)}
                  </select>
                </div>
                {/* Additional Sub-Category */}
                {filteredAdditionalSubCategories.length > 0 && (
                  <div style={{ gridColumn: 'span 2' }}>
                    <label style={lStyle}>Additional Sub-Category</label>
                    <select value={form.additionalSubCategory} onChange={e => setForm(f => ({ ...f, additionalSubCategory: e.target.value }))} style={inputStyle()}>
                      <option value="">— Select Additional Sub-Category —</option>
                      {filteredAdditionalSubCategories.map(c => <option key={c.id} value={c.value}>{c.value}</option>)}
                    </select>
                  </div>
                )}
                {/* Attachments */}
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={lStyle}>Attachments</label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '9px 12px', border: '1.5px dashed #C7D2FE', borderRadius: 9, background: '#F8FAFF', fontSize: 13, color: '#6B7280' }}>
                    <Icon name="attach" size={15} color="#6B7280" />
                    <span>{attachmentFiles.length > 0 ? `${attachmentFiles.length} file(s) selected` : 'Click to attach files'}</span>
                    <input type="file" multiple style={{ display: 'none' }} onChange={e => setAttachmentFiles(Array.from(e.target.files))} />
                  </label>
                  {attachmentFiles.length > 0 && (
                    <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {attachmentFiles.map((f, i) => (
                        <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, background: '#EEF2FF', color: '#3B5BDB', borderRadius: 6, padding: '3px 8px', fontWeight: 600 }}>
                          {f.name}
                          <button type="button" onClick={() => setAttachmentFiles(prev => prev.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 1, color: '#6B7280', fontSize: 13 }}>×</button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div style={{ marginTop: 18, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button type="button" onClick={() => setShowForm(false)} style={{ padding: '9px 18px', border: '1px solid #E2E8F0', borderRadius: 9, background: '#fff', cursor: 'pointer', fontSize: 13, color: '#374151', fontFamily: 'inherit' }}>Cancel</button>
                <button type="submit" disabled={saving} style={{ padding: '9px 22px', border: 'none', borderRadius: 9, background: saving ? '#93C5FD' : 'var(--accent)', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' }}>{saving ? 'Submitting...' : 'Submit Ticket'}</button>
              </div>
            </form>
          </div>
        )}

        {/* ── Filters ────────────────────────────────────────────────────── */}
        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #E2E8F0', padding: '14px 16px', marginBottom: 16, boxShadow: '0 1px 4px rgba(59,91,219,0.05)' }}>

          {/* Row 1: Search + Status tabs */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
            <div style={{ position: 'relative', flex: '1 1 200px', minWidth: 160 }}>
              <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}><Icon name="search" size={14} color="#9CA3AF" /></span>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search tickets..." style={{ width: '100%', padding: '8px 10px 8px 32px', border: '1px solid #E2E8F0', borderRadius: 9, fontSize: 13, background: '#F8FAFF', outline: 'none', color: '#374151', fontFamily: 'inherit', boxSizing: 'border-box' }} />
            </div>
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
              {STATUS_FILTERS.map(s => (
                <button key={s} onClick={() => setStatus(s)} style={{ padding: '6px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, border: statusFilter === s ? 'none' : '1px solid #E2E8F0', background: statusFilter === s ? 'var(--accent)' : '#F8FAFF', color: statusFilter === s ? '#fff' : '#6B7280', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' }}>{s}</button>
              ))}
            </div>
          </div>

          {/* Divider */}
          <div style={{ height: 1, background: '#F1F5F9', marginBottom: 10 }} />

          {/* Row 2: Date presets + Priority + Clear */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {/* Date label */}
            <span style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.07em', flexShrink: 0 }}>Date:</span>

            {/* Quick preset buttons */}
            {DATE_PRESETS.map(label => (
              <button key={label} onClick={() => handlePreset(label)}
                style={{ padding: '5px 11px', borderRadius: 7, fontSize: 12, fontWeight: 600, border: `1.5px solid ${datePreset === label ? 'var(--accent)' : '#E2E8F0'}`, background: datePreset === label ? '#EEF2FF' : '#F8FAFF', color: datePreset === label ? 'var(--accent)' : '#6B7280', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s', flexShrink: 0 }}>
                {label}
              </button>
            ))}

            {/* Custom range toggle */}
            <button onClick={handleCustomToggle}
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 11px', borderRadius: 7, fontSize: 12, fontWeight: 600, border: `1.5px solid ${datePreset === 'custom' ? 'var(--accent)' : '#E2E8F0'}`, background: datePreset === 'custom' ? '#EEF2FF' : '#F8FAFF', color: datePreset === 'custom' ? 'var(--accent)' : '#6B7280', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s', flexShrink: 0 }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              Custom Range
            </button>

            {/* Separator */}
            <div style={{ width: 1, height: 20, background: '#E2E8F0', flexShrink: 0 }} />

            {/* Priority filter */}
            <span style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.07em', flexShrink: 0 }}>Priority:</span>
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
              {PRIORITY_OPTS.map(p => {
                const pColors = { Low: '#6B7280', Medium: '#D97706', High: '#F97316', Urgent: '#E53E3E' };
                const isActive = priorityFilter === p;
                const dotColor = pColors[p] || 'var(--accent)';
                return (
                  <button key={p} onClick={() => setPriority(p)}
                    style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 11px', borderRadius: 7, fontSize: 12, fontWeight: 600, border: `1.5px solid ${isActive ? dotColor : '#E2E8F0'}`, background: isActive ? `${dotColor}18` : '#F8FAFF', color: isActive ? dotColor : '#6B7280', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' }}>
                    {p !== 'All' && <span style={{ width: 6, height: 6, borderRadius: '50%', background: isActive ? dotColor : '#D1D5DB', display: 'inline-block', flexShrink: 0 }} />}
                    {p}
                  </button>
                );
              })}
            </div>

            {/* Clear all button */}
            {activeFilterCount > 0 && (
              <button onClick={clearAllFilters}
                style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 'auto', padding: '5px 11px', borderRadius: 7, fontSize: 12, fontWeight: 600, border: '1.5px solid #FCA5A5', background: '#FEF2F2', color: '#EF4444', cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                Clear Filters ({activeFilterCount})
              </button>
            )}
          </div>

          {/* Row 3: Custom date range inputs (shown when custom selected) */}
          {showCustomRange && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.07em' }}>From:</span>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                style={{ padding: '6px 10px', border: '1.5px solid #E2E8F0', borderRadius: 7, fontSize: 13, color: '#374151', background: '#F8FAFF', outline: 'none', fontFamily: 'inherit', cursor: 'pointer' }} />
              <span style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.07em' }}>To:</span>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                min={dateFrom || undefined}
                style={{ padding: '6px 10px', border: '1.5px solid #E2E8F0', borderRadius: 7, fontSize: 13, color: '#374151', background: '#F8FAFF', outline: 'none', fontFamily: 'inherit', cursor: 'pointer' }} />
              {(dateFrom || dateTo) && (
                <button onClick={() => { setDateFrom(''); setDateTo(''); }}
                  style={{ padding: '5px 10px', borderRadius: 7, fontSize: 12, border: '1px solid #E2E8F0', background: '#fff', color: '#9CA3AF', cursor: 'pointer', fontFamily: 'inherit' }}>
                  Reset
                </button>
              )}
              {/* Active range label */}
              {(dateFrom || dateTo) && (
                <span style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 600 }}>
                  {dateFrom && dateTo ? `${dateFrom} to ${dateTo}` : dateFrom ? `From ${dateFrom}` : `Until ${dateTo}`}
                </span>
              )}
            </div>
          )}

          {/* Active filter chips */}
          {activeFilterCount > 0 && (
            <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 600 }}>Active:</span>
              {datePreset && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 9px', borderRadius: 20, background: '#EEF2FF', color: 'var(--accent)', fontSize: 11, fontWeight: 700 }}>
                  {datePreset === 'custom'
                    ? `Custom${dateFrom || dateTo ? ': ' + (dateFrom || '...') + ' - ' + (dateTo || '...') : ''}`
                    : datePreset}
                  <button onClick={() => { setDatePreset(null); setShowCustomRange(false); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', color: 'var(--accent)' }}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                </span>
              )}
              {priorityFilter !== 'All' && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 9px', borderRadius: 20, background: '#FFF7ED', color: '#D97706', fontSize: 11, fontWeight: 700 }}>
                  Priority: {priorityFilter}
                  <button onClick={() => setPriority('All')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', color: '#D97706' }}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                </span>
              )}
              <span style={{ fontSize: 11, color: '#9CA3AF' }}>
                Showing {filteredTickets.length} of {tickets.length} tickets
              </span>
            </div>
          )}
        </div>

        {/* Ticket Table */}
        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #E2E8F0', boxShadow: '0 2px 8px rgba(59,91,219,0.06)', overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr 130px 90px 170px', padding: '10px 20px', borderBottom: '1px solid #F1F5F9', background: '#F8FAFF' }}>
            {['Ticket #', 'Subject', 'Status', 'Priority', 'Created'].map(h => (
              <span key={h} style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{h}</span>
            ))}
          </div>
          {loading ? (
            <div style={{ padding: '48px 20px', textAlign: 'center', color: '#9CA3AF', fontSize: 14 }}>Loading...</div>
          ) : filteredTickets.length === 0 ? (
            <div style={{ padding: '60px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>{tickets.length === 0 ? '' : ''}</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                {tickets.length === 0 ? 'No tickets yet' : 'No tickets match the current filters'}
              </div>
              <div style={{ fontSize: 13, color: '#9CA3AF' }}>
                {tickets.length === 0
                  ? 'Click "New Ticket" to submit your first support request.'
                  : 'Try adjusting the date range, status, or priority filters.'}
              </div>
              {activeFilterCount > 0 && (
                <button onClick={clearAllFilters} style={{ marginTop: 14, padding: '8px 18px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Clear Filters</button>
              )}
            </div>
          ) : (
            filteredTickets.map((t, idx) => (
              <div key={t.id} onClick={() => handleSelectTicket(t)}
                style={{ display: 'grid', gridTemplateColumns: '130px 1fr 130px 90px 170px', padding: '13px 20px', borderBottom: idx < filteredTickets.length - 1 ? '1px solid #F1F5F9' : 'none', cursor: 'pointer', transition: 'background 0.12s', background: selected?.id === t.id ? '#EEF2FF' : '#fff' }}
                onMouseEnter={e => { if (selected?.id !== t.id) e.currentTarget.style.background = '#F8FAFF'; }}
                onMouseLeave={e => { if (selected?.id !== t.id) e.currentTarget.style.background = '#fff'; }}
              >
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', fontFamily: 'monospace' }}>{t.ticket_number || `#${t.id}`}</span>
                <span style={{ fontSize: 13, color: '#1A202C', fontWeight: 500, paddingRight: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.subject}</span>
                <div><StatusBadge status={t.status} /></div>
                <div><PriorityBadge priority={t.priority} /></div>
                <span style={{ fontSize: 12, color: '#9CA3AF' }}>{t._created_at ? fmt(t._created_at) : (t.created || '"')}</span>
              </div>
            ))
          )}
        </div>
      </main>
      )}

      {activeSection === 'faq' && <UserFAQBrowser />}

      {/* Ticket Detail + Conversation Drawer */}
      {selected && (
        <TicketDrawer
          ticket={selected}
          currentUser={currentUser}
          onClose={() => setSelected(null)}
          fmt={fmt}
          onTicketUpdated={(updated) => {
            setSelected(updated);
            setTickets(ts => ts.map(t => t.id === updated.id ? updated : t));
          }}
          onReload={load}
        />
      )}

      {/* CSAT Feedback Modal */}
      {csatTicket && (
        <CsatModal
          ticket={csatTicket}
          onClose={() => setCsatTicket(null)}
          onSaved={(updated) => {
            setCsatTicket(null);
            setSelected(updated);
            setTickets(ts => ts.map(t => t.id === updated.id ? updated : t));
            toast('Thank you for your feedback!');
          }}
        />
      )}

      <FloatingAssistantsHub
        displayName={currentUser?.name || ''}
        prefilledEmail={currentUser?.email || ''}
        chatbotProps={REQUESTER_CHAT_CONFIG}
        voiceProps={REQUESTER_VOICE_CONFIG}
      />
    </div>
  );
}

/* --- Ticket Detail + Conversation Drawer ------------------------------------ */
function TicketDrawer({ ticket: initialTicket, currentUser, onClose, fmt, onTicketUpdated, onReload }) {
  const [ticket, setTicket]       = useState(initialTicket);
  const [convos, setConvos]       = useState([]);
  const [convLoading, setConvLoading] = useState(true);
  const [reply, setReply]         = useState('');
  const [sending, setSending]     = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    setTicket(initialTicket);
    loadConvos(initialTicket.id);
  }, [initialTicket.id]); // eslint-disable-line

  const loadConvos = async (id) => {
    setConvLoading(true);
    try { setConvos((await conversationsAPI.list(id)) || []); }
    catch { /* ignore */ }
    finally { setConvLoading(false); }
  };

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [convos]);

  const isClosed = CLOSED_STATUSES.includes(ticket.status);

  const handleSendReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      const msg = await conversationsAPI.create(ticket.id, { mode: 'requester_reply', body: reply.trim() });
      setConvos(c => [...c, msg]);
      setReply('');
      // Always refresh ticket (status may have changed, updated_at updated)
      const refreshed = await ticketsAPI.get(ticket.id);
      setTicket(refreshed);
      onTicketUpdated(refreshed);
      onReload();
      toast('Reply sent successfully');
    } catch (err) {
      toast(err.message || 'Failed to send reply', 'error');
    } finally {
      setSending(false);
    }
  };

  const visibleConvos = convos.filter(c => c.mode !== 'note' && c.mode !== 'ack');

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 100 }} />
      <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 500, background: '#fff', zIndex: 101, display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)' }}>

        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #F1F5F9', background: '#F8FAFF', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{ticket.ticket_number}</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#1A202C', marginTop: 3, lineHeight: 1.4 }}>{ticket.subject}</div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <StatusBadge status={ticket.status} />
                <PriorityBadge priority={ticket.priority} />
              </div>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, flexShrink: 0 }}><Icon name="close" size={18} color="#9CA3AF" /></button>
          </div>
        </div>

        {/* Scrollable body */}
        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>

          {/* Meta */}
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #F1F5F9' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
              {[
                { label: 'Type',       value: ticket.type || '"' },
                { label: 'Category',   value: ticket.category || '"' },
                { label: 'Assigned',   value: ticket.assignedTo || 'Unassigned' },
                { label: 'Due By',     value: ticket.due_by ? fmt(ticket.due_by) : '"' },
                { label: 'Created',    value: ticket._created_at ? fmt(ticket._created_at) : (ticket.created || '"') },
                { label: 'Department', value: ticket.dept || ticket.department || '—' },
                { label: 'Org Unit', value: ticket.org_unit || ticket.orgUnit || '—' },
                { label: 'Head of Dept', value: ticket.head_of_department || ticket.headOfDepartment || '—' },
              ].map(f => (
                <div key={f.label}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>{f.label}</div>
                  <div style={{ fontSize: 12, color: '#374151' }}>{f.value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Description (HTML-safe render) */}
          {ticket.description && (
            <div style={{ padding: '14px 20px', borderBottom: '1px solid #F1F5F9' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Description</div>
              <div style={{ background: '#F8FAFF', borderRadius: 8, padding: '10px 12px', border: '1px solid #E2E8F0', maxHeight: 200, overflowY: 'auto' }}>
                <SafeHtml html={ticket.description} />
              </div>
            </div>
          )}

          {/* CSAT already submitted */}
          {ticket.request_rating && (
            <div style={{ margin: '12px 20px', background: '#ECFDF5', borderRadius: 10, padding: '10px 14px', border: '1px solid #A7F3D0' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#059669', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Your Feedback</div>
              <div style={{ display: 'flex', gap: 2 }}>
                {[1,2,3,4,5].map(n => <span key={n} style={{ fontSize: 18, color: n <= ticket.request_rating ? '#F59E0B' : '#D1D5DB' }}>{n <= ticket.request_rating ? '\u2605' : '\u2606'}</span>)}
              </div>
              {ticket.csat_remarks && <div style={{ fontSize: 12, color: '#374151', marginTop: 4 }}>{ticket.csat_remarks}</div>}
            </div>
          )}

          {/* Conversation thread */}
          <div style={{ padding: '14px 20px', flex: 1 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>
              Conversation ({visibleConvos.length})
            </div>
            {convLoading ? (
              <div style={{ textAlign: 'center', color: '#9CA3AF', fontSize: 13, padding: '20px 0' }}>Loading...</div>
            ) : visibleConvos.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#9CA3AF', fontSize: 13, padding: '16px 0' }}>No messages yet</div>
            ) : (
              visibleConvos.map((c, idx) => {
                const isMe = c.mode === 'requester_reply' || c.sender_id === currentUser.id;
                return (
                  <div key={c.id || idx} style={{ display: 'flex', flexDirection: isMe ? 'row-reverse' : 'row', gap: 8, marginBottom: 14, alignItems: 'flex-start' }}>
                    <Avatar name={isMe ? currentUser.name : (c.sender_name || 'Support')} size={28} />
                    <div style={{ maxWidth: '78%' }}>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexDirection: isMe ? 'row-reverse' : 'row', marginBottom: 3 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#374151' }}>{isMe ? 'You' : (c.sender_name || 'Support')}</span>
                        <span style={{ fontSize: 10, color: '#9CA3AF' }}>{c.created_at ? fmt(c.created_at) : ''}</span>
                      </div>
                      <div style={{ background: isMe ? 'var(--accent)' : '#F1F5F9', color: isMe ? '#fff' : '#1A202C', borderRadius: isMe ? '14px 4px 14px 14px' : '4px 14px 14px 14px', padding: '10px 14px', fontSize: 13, lineHeight: 1.5 }}>
                        <SafeHtml html={c.body} />
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={bottomRef} />
          </div>
        </div>

        {/* Reply box */}
        <div style={{ borderTop: '1px solid #E2E8F0', padding: '14px 20px', background: '#fff', flexShrink: 0 }}>
          {isClosed && (
            <div style={{ fontSize: 11, color: '#D97706', background: '#FFFBEB', borderRadius: 6, padding: '4px 10px', marginBottom: 8, fontWeight: 600 }}>
              This ticket is {ticket.status}. Your reply will reopen it.
            </div>
          )}
          <textarea value={reply} onChange={e => setReply(e.target.value)} placeholder="Type your reply... (Ctrl+Enter to send)" rows={3}
            style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #E2E8F0', borderRadius: 8, fontSize: 13, resize: 'vertical', fontFamily: 'inherit', color: '#1A202C', outline: 'none', boxSizing: 'border-box', minHeight: 72 }}
            onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSendReply(); }}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            <button onClick={handleSendReply} disabled={sending || !reply.trim()}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 18px', background: reply.trim() ? 'var(--accent)' : '#E2E8F0', color: reply.trim() ? '#fff' : '#9CA3AF', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: reply.trim() ? 'pointer' : 'default', fontFamily: 'inherit' }}>
              <Icon name="send" size={13} color={reply.trim() ? '#fff' : '#9CA3AF'} />
              {sending ? 'Sending...' : 'Send Reply'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

/* --- CSAT Feedback Modal ------------------------------------------------------ */
function CsatModal({ ticket, onClose, onSaved }) {
  const [rating, setRating]   = useState(0);
  const [hovered, setHovered] = useState(0);
  const [remarks, setRemarks] = useState('');
  const [saving, setSaving]   = useState(false);

  const LABELS = ['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'];
  const COLORS = ['', '#EF4444', '#F97316', '#EAB308', '#22C55E', '#059669'];

  const handleSubmit = async () => {
    if (!rating) return;
    setSaving(true);
    try {
      const updated = await ticketsAPI.update(ticket.id, { request_rating: rating, csat_remarks: remarks.trim() || undefined });
      onSaved(updated);
    } catch (err) {
      toast(err.message || 'Failed to save feedback', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 20, width: '100%', maxWidth: 420, padding: '32px 32px 28px', boxShadow: '0 20px 60px rgba(0,0,0,0.18)', textAlign: 'center' }}>
        <div style={{ fontSize: 40, marginBottom: 8 }}></div>
        <div style={{ fontSize: 18, fontWeight: 800, color: '#1A202C', marginBottom: 6 }}>Your ticket is {ticket.status}!</div>
        <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 24 }}>
          How would you rate the support you received for<br />
          <strong>{ticket.ticket_number} &mdash; {ticket.subject}</strong>?
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginBottom: 8 }}>
          {[1,2,3,4,5].map(n => (
            <button key={n} onClick={() => setRating(n)} onMouseEnter={() => setHovered(n)} onMouseLeave={() => setHovered(0)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 38, color: n <= (hovered || rating) ? COLORS[hovered || rating] : '#D1D5DB', lineHeight: 1, transition: 'color 0.12s, transform 0.12s', transform: n <= (hovered || rating) ? 'scale(1.15)' : 'scale(1)' }}>
              {'\u2605'}
            </button>
          ))}
        </div>
        {(hovered || rating) > 0 && (
          <div style={{ fontSize: 13, fontWeight: 700, color: COLORS[hovered || rating], marginBottom: 16 }}>{LABELS[hovered || rating]}</div>
        )}

        <textarea value={remarks} onChange={e => setRemarks(e.target.value)} placeholder="Any additional comments? (optional)" rows={3}
          style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #E2E8F0', borderRadius: 8, fontSize: 13, resize: 'none', fontFamily: 'inherit', color: '#1A202C', outline: 'none', boxSizing: 'border-box', marginBottom: 18 }} />

        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button onClick={onClose} style={{ padding: '10px 22px', border: '1px solid #E2E8F0', borderRadius: 10, background: '#fff', cursor: 'pointer', fontSize: 13, color: '#6B7280', fontFamily: 'inherit' }}>Skip</button>
          <button onClick={handleSubmit} disabled={!rating || saving}
            style={{ padding: '10px 28px', border: 'none', borderRadius: 10, background: rating ? COLORS[rating] : '#E2E8F0', color: rating ? '#fff' : '#9CA3AF', cursor: rating ? 'pointer' : 'default', fontSize: 13, fontWeight: 700, fontFamily: 'inherit' }}>
            {saving ? 'Saving...' : 'Submit Feedback'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* --- Style helpers ------------------------------------------------------------ */
const lStyle    = { fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6, display: 'block' };
const errStyle  = { fontSize: 11, color: '#E53E3E', marginTop: 4 };
function inputStyle(hasError = false) {
  return { width: '100%', padding: '9px 12px', boxSizing: 'border-box', border: `1.5px solid ${hasError ? '#FC8181' : '#E2E8F0'}`, borderRadius: 8, fontSize: 13, color: '#1A202C', background: hasError ? '#FFF5F5' : '#FAFAFA', outline: 'none', fontFamily: 'inherit' };
}
